import type { User } from '@/context/AuthContext';

const normalizeRoleValue = (role?: string | null): string =>
  typeof role === 'string' ? role.trim().toUpperCase() : '';

export const ADMIN_ROLE_SLUGS = [
  'SYSTEM_ADMIN',
  'ADMIN',
] as const;

export type AdminRole = (typeof ADMIN_ROLE_SLUGS)[number];

const ADMIN_ROLE_SLUG_SET = new Set<string>(ADMIN_ROLE_SLUGS);

// RBAC role slugs (from admin-role-permissions.md §2)
export const RBAC_ROLE_SLUGS = [
  'SYSTEM_ADMIN',
  'ADMIN',
  'AGENCY_OWNER',
  'AGENCY_MANAGER',
  'ADVISOR',
  'READ_ONLY',
] as const;

export type RbacRole = (typeof RBAC_ROLE_SLUGS)[number];

// Roles that can manage users within their agency
export const AGENCY_MANAGEMENT_ROLES = ['AGENCY_OWNER', 'AGENCY_MANAGER'] as const;
const AGENCY_MANAGEMENT_ROLE_SET = new Set<string>(AGENCY_MANAGEMENT_ROLES);

export const normalizeRole = (role?: string | null): string =>
  normalizeRoleValue(role);

export const isSystemAdminRole = (role?: string | null): boolean =>
  normalizeRoleValue(role) === 'SYSTEM_ADMIN';

export const isAdminRole = (role?: string | null): boolean =>
  ADMIN_ROLE_SLUG_SET.has(normalizeRoleValue(role));

export const isAgencyOwnerRole = (role?: string | null): boolean =>
  normalizeRoleValue(role) === 'AGENCY_OWNER';

export const isAgencyManagerRole = (role?: string | null): boolean =>
  normalizeRoleValue(role) === 'AGENCY_MANAGER';

export const canManageAgencyUsers = (role?: string | null): boolean =>
  AGENCY_MANAGEMENT_ROLE_SET.has(normalizeRoleValue(role)) ||
  isSystemAdminRole(role);

export const canAccessAdmin = (
  user: Pick<User, 'role'> | null | undefined,
): user is Pick<User, 'role'> & { role: AdminRole } =>
  !!user && ADMIN_ROLE_SLUG_SET.has(normalizeRoleValue(user.role));
